/**
 * 睡眠レポート API
 */

import { apiClient } from '@/api/client'
import type {
  SleepDayDetail,
  SleepDayListResponse,
  SleepEpisodeRaw,
  SleepPlanResponse,
  SleepPlanWindowsResponse,
  SleepSettings,
  SleepTrendsResponse,
  TodayAdviceResponse,
} from '@/api/types/sleep'

export const getSleepDays = async (days = 60): Promise<SleepDayListResponse> => {
  const res = await apiClient.get('/sleep/days', { params: { days } })
  return res.data
}

/** date は起床日（JST, YYYY-MM-DD） */
export const getSleepDay = async (date: string): Promise<SleepDayDetail> => {
  const res = await apiClient.get(`/sleep/days/${date}`)
  return res.data
}

/** AI 解析をやり直す（Gemini を呼ぶので数十秒かかることがある） */
export const analyzeSleepDay = async (date: string): Promise<SleepDayDetail> => {
  const res = await apiClient.post(`/sleep/days/${date}/analyze`, null, { timeout: 180_000 })
  return res.data
}

/** SpO2・呼吸の対策を AI で作る（毎日は作らず、画面を開いたときにまだ無ければ呼ぶ） */
export const adviseSleepBreathing = async (date: string): Promise<SleepDayDetail> => {
  const res = await apiClient.post(`/sleep/days/${date}/breathing-advice`, null, { timeout: 180_000 })
  return res.data
}

/** 睡眠エピソードの Garmin 応答そのまま（生データの表示用） */
export const getSleepEpisodeRaw = async (episodeId: number): Promise<SleepEpisodeRaw> => {
  const res = await apiClient.get(`/sleep/episodes/${episodeId}/raw`)
  return res.data
}

export const getSleepTrends = async (days = 30): Promise<SleepTrendsResponse> => {
  const res = await apiClient.get('/sleep/trends', { params: { days } })
  return res.data
}

/** 次の睡眠の計画（カレンダーを読むので数十秒かかることがある。cached なら数分以内に作った計画を使う） */
export const getSleepPlan = async (cached = false): Promise<SleepPlanResponse> => {
  const res = await apiClient.get('/sleep/plan', { params: { cached }, timeout: 60_000 })
  return res.data
}

/** 期間内の各晩の睡眠の予定（カレンダー表示用） */
export const getSleepPlanWindows = async (from: string, to: string): Promise<SleepPlanWindowsResponse> => {
  const res = await apiClient.get('/sleep/plan/windows', { params: { from, to }, timeout: 60_000 })
  return res.data
}

export const updateSleepSettings = async (settings: SleepSettings): Promise<SleepSettings> => {
  const res = await apiClient.put('/sleep/settings', settings)
  return res.data
}

/** 今日作った過ごし方の提案（まだ作っていなければ null） */
export const getTodayAdvice = async (): Promise<TodayAdviceResponse | null> => {
  const res = await apiClient.get('/sleep/today')
  return res.data
}

/** 今の体調と予定から作り直す（カレンダーと Gemini を呼ぶので数十秒かかる） */
export const createTodayAdvice = async (): Promise<TodayAdviceResponse> => {
  const res = await apiClient.post('/sleep/today', null, { timeout: 180_000 })
  return res.data
}
